import React from 'react';
import { Alert, Box, Button, Card, CardContent, Snackbar, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { ApprovalRequest, deriveApprovalStatus, useGetApprovalRequestQuery } from '../../../services/ApprovalService';
import { ApprovalActionBar, ApprovalErrorState, ApprovalLoadingState, ApprovalSummary, CommentEditor, ConfirmActionDialog, EntitySection } from '../components';
import { useApprovalActions } from '../hooks/useApprovalActions';

const ApprovalDetailPage: React.FC = () => {
  const { approvalRequestId = '' } = useParams<{ approvalRequestId: string }>();
  const navigate = useNavigate();

  const { data: approval, isLoading, isError, error, refetch } =
    useGetApprovalRequestQuery(approvalRequestId, { skip: !approvalRequestId });

  if (isLoading) return <ApprovalLoadingState />;
  if (isError || !approval) {
    const notFound = (error as { status?: number })?.status === 404;
    return <ApprovalErrorState notFound={notFound} onRetry={refetch} />;
  }

  return <ApprovalDetailContent approval={approval} onBack={() => navigate('/approvals')} />;
};

interface ApprovalDetailContentProps {
  approval: ApprovalRequest;
  onBack: () => void;
}

const ApprovalDetailContent: React.FC<ApprovalDetailContentProps> = ({ approval, onBack }) => {
  const status = deriveApprovalStatus(approval);
  const isActionable = status === 'pending';

  const {
    comment, setComment,
    pendingAction, setPendingAction,
    actionError, snack, setSnack,
    submitting, commentMissing, runAction,
  } = useApprovalActions(approval);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', py: 1.5, px: 2 }}>
      <Box display="flex" justifyContent="center">
        <Card
          sx={{
            maxWidth: 900,
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#ffffff', borderBottom: '2px solid #3b82f6', p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#1e293b' }}>
                  Approval Request
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                  Review the request and approve or reject.
                </Typography>
              </Box>
              <Button startIcon={<ArrowBack />} onClick={onBack} size="small">
                Inbox
              </Button>
            </Stack>
          </Box>

          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Stack gap={2}>
              <EntitySection approval={approval} />
              <ApprovalSummary approval={approval} />

              {!isActionable && (
                <Alert severity="info">
                  This request has already been resolved (status: {status}).
                </Alert>
              )}

              {isActionable && (
                <CommentEditor
                  value={comment}
                  onChange={setComment}
                  required={approval.requireComment}
                />
              )}

              {actionError && <Alert severity="error">{actionError}</Alert>}

              {isActionable && (
                <ApprovalActionBar
                  disabled={submitting || commentMissing}
                  onApprove={() => setPendingAction('approve')}
                  onReject={() => setPendingAction('reject')}
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <ConfirmActionDialog
        action={pendingAction}
        submitting={submitting}
        onClose={() => setPendingAction(null)}
        onConfirm={runAction}
      />

      <Snackbar
        open={snack !== null}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
      />
    </Box>
  );
};

export default ApprovalDetailPage;
